import { Link } from 'react-router-dom';
import { Building2, UserCheck, UserX, Users, Zap, MapPin } from 'lucide-react';
import type { OrganizationListItem, CustomerStatus } from '../../types';
import { CustomerStatusBadge } from './CustomerStatusBadge';
import { FavoriteStar } from '../lists';
import { orgTypeToListKind } from '../../lib/orgTypes';

interface OrganizationCardProps {
  organization: OrganizationListItem;
  coachCount: number;
}

const BORDER_COLOR: Record<CustomerStatus, string> = {
  PROSPECT: 'border-l-gray-400',
  ONBOARDED: 'border-l-blue-500',
  ACTIVE: 'border-l-green-500',
  SLIPPING: 'border-l-amber-500',
  LAPSED: 'border-l-red-500',
};

export const OrganizationCard = ({ organization: org, coachCount }: OrganizationCardProps) => {
  const borderColor = BORDER_COLOR[org.customerStatus] ?? 'border-l-gray-400';
  const location = [org.city, org.state].filter(Boolean).join(', ');

  return (
    <Link to={`/organizations/${org.id}`} className="block">
      <div
        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow border-l-4 ${borderColor}`}
      >
        {/* Row 1: Name + customer-status badge */}
        <div className="flex justify-between items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5 min-w-0">
            <Building2 className="h-4 w-4 text-purple-500 dark:text-purple-400 flex-shrink-0" />
            <span className="truncate">{org.name}</span>
          </span>
          <span className="flex-shrink-0 flex items-center gap-1">
            <CustomerStatusBadge status={org.customerStatus} size="sm" />
            <FavoriteStar kind={orgTypeToListKind(org.type)} id={org.id} size={20} />
          </span>
        </div>

        {/* Row 2: City, State */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
          <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate">{location || '—'}</span>
        </div>

        {/* Row 3: assigned rep */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
          {org.assignedRep ? (
            <>
              <UserCheck className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{org.assignedRep.displayName}</span>
            </>
          ) : (
            <>
              <UserX className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span>Open</span>
            </>
          )}
        </div>

        {/* Row 4: Coach count + last-fundraiser summary */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 gap-2">
          <span className="flex items-center gap-1.5 flex-shrink-0">
            {coachCount > 0 ? (
              <>
                <Users className="h-3 w-3 text-gray-400" />
                <span>
                  {coachCount} coach{coachCount === 1 ? '' : 'es'}
                </span>
              </>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">No coaches yet</span>
            )}
          </span>
          {org.lastFundraiserLaunchedAt && (
            <span className="text-green-600 dark:text-green-400 text-right flex items-center gap-1 min-w-0">
              <Zap className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">
                Last:{' '}
                {new Date(org.lastFundraiserLaunchedAt).toLocaleDateString('en-US', {
                  month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC',
                })}
                {org.lastFundraiserAmountCents && (
                  <> · ${Math.round(Number(BigInt(org.lastFundraiserAmountCents)) / 100).toLocaleString('en-US')}</>
                )}
              </span>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
